<script setup lang="ts">
import { computed } from "vue";
import { logoTile } from "@pinksy/contracts";
import { initials } from "../identity";
import { companyLook, type CompanyLookSource, type LookFallback } from "../look";

/**
 * 🔴 **A COMPANY, IN ITS OWN COLOURS** (D77 × PNK-109).
 *
 * Devin: *"when an org is selected or viewed it's got to get their look and feel."*
 *
 * Their mark when the bytes have been fetched; their monogram when they have none — on a tile tinted
 * with THEIR accent, read off their own website by the research engine and accepted by a person.
 * A company nobody has researched falls back to the tenant's colour, which is the correct answer and
 * the reason `look.ts` writes no colour of its own.
 *
 * 🔴 **THE TINT IS NOT A TINT INDEX.** `PinkFace` gives a company one of six generated tints derived
 * from its name, so a reader can learn a row. That is still right where nothing is known. Here we
 * know something better: the colour the company actually uses. When we do, it wins.
 *
 * 🔴 **A picture a human looks at.** Nothing measures, matches, ranks or filters on it (D15).
 *
 * This component does NOT fetch — `useMarks` does, because only the module knows which API client
 * carries the session's bearer. It is handed an object URL, or null.
 *
 * 🔴 **THE TILE UNDER THEIR LOGO IS THE ONE THE LOGO ASKS FOR** (D139). Devin: *"when we bring back
 * logos, we should always look at them and say whether they'll look good with a white or a dark
 * background if there is no background."* The backend measured it; `look.logoBackground` carries the
 * word. `light` sits on white and `dark` on the Desk's dark surface in EITHER theme — a black
 * wordmark on a dark Desk was an invisible logo — and `own`/`either`/not-yet stay as they always were.
 */
const props = withDefaults(
  defineProps<{
    name: string | null;
    /** The company's brand row, straight off any organisation read. Null draws the tenant's colour. */
    look?: CompanyLookSource | null;
    /** An object URL from the module's `useMarks`, or null when there is nothing to draw. */
    src?: string | null;
    size?: number;
    /** `square` is a company (a squircle, like `PinkFace`); `bar` is a 3px rule for a dense row. */
    shape?: "square" | "bar";
    /** Overrides the title. Pass "" to say nothing — inside a button that has its own label. */
    title?: string;
    /** What the logo reads on (D139). Wins over `look.logoBackground` when given. */
    background?: string | null;
    /**
     * 🔴 What to draw below their palette (D164 rule 5) — the accent their logo gave, then their name's tint.
     * `PinkCompanyBadge` always passes it; without it the tile keeps the tenant fallback it always had.
     */
    lookFallback?: LookFallback | null;
  }>(),
  { look: null, src: null, size: 32, shape: "square", title: undefined, background: undefined, lookFallback: null },
);

const look = computed(() => companyLook(props.look, props.lookFallback));
const shown = computed(() => props.title ?? (props.name ?? "This company"));
const drawsImage = computed(() => props.src !== null && props.shape !== "bar");
const verdict = computed(() => (props.background !== undefined ? props.background : (props.look?.logoBackground ?? null)));
/** The tile colour the verdict asks for — only under an IMAGE; a monogram keeps their accent wash. */
const tile = computed(() => (drawsImage.value ? logoTile(verdict.value) : null));
const box = computed(() => ({
  ...look.value.style,
  ...(tile.value === null ? {} : { "--pk-mark-tile": tile.value }),
  width: props.shape === "bar" ? "3px" : `${props.size}px`,
  height: `${props.size}px`,
  // The monogram holds its proportion as the tile grows, and never falls below two legible letters.
  fontSize: `${Math.max(10, Math.round(props.size * 0.38))}px`,
}));
</script>

<template>
  <span
    class="pk-mark"
    :class="[`pk-mark--${props.shape}`, { 'pk-mark--theirs': look.theirs, 'pk-mark--img': props.src !== null }]"
    :style="box"
    :title="shown === '' ? undefined : shown"
    :data-mark-theirs="look.theirs ? 'yes' : 'no'"
    :data-logo-background="drawsImage ? (verdict ?? undefined) : undefined"
    data-testid="mark"
  >
    <img v-if="drawsImage && props.src !== null" :src="props.src" :alt="props.name ?? 'Company'" class="pk-mark__img" />
    <span v-else-if="props.shape !== 'bar'" class="pk-mark__initials" aria-hidden="true">{{ initials(props.name, "organisation") }}</span>
  </span>
</template>
